import { useState } from 'react'
import type { ConversationListItemDTO } from '@sac/contracts'
import { useAuth } from '../state/AuthContext'
import { Icon } from './icons'
import { Menu } from './ui/Menu'
import { useToast } from './ui/Toaster'
import { ChangePassword } from './ChangePassword'
import { AgentAvatar } from './AgentAvatar'
import { AvatarPicker } from './AvatarPicker'

const STATUS_OPTIONS: [string, string][] = [
  ['Disponível', '#12a150'],
  ['Pausa', '#e8a33d'],
  ['Ausente', '#c2751f'],
  ['Offline', '#8b919d'],
]

export function TopBar({
  children,
  items,
  onOpenSection,
}: {
  children: React.ReactNode
  items: ConversationListItemDTO[]
  onOpenSection: (s: string) => void
}) {
  const { user, logout } = useAuth()
  const toast = useToast()
  const [status, setStatus] = useState('Disponível')
  const [changingPassword, setChangingPassword] = useState(false)
  const [changingPhoto, setChangingPhoto] = useState(false)
  const statusColor = STATUS_OPTIONS.find(([s]) => s === status)?.[1] ?? '#12a150'

  const slaBreaches = items.filter((i) => i.slaBreached)
  const unassigned = items.filter((i) => !i.assignedAgentId && i.status !== 'resolved')

  return (
    <header className="topbar">
      {children}
      <div className="topbar-right">
        <div className="ai-chip">
          <Icon name="spark" size={13} />
          IA aprendendo
          <span className="tip">A IA está aprendendo com o atendimento da equipe. Nenhuma mensagem é enviada automaticamente.</span>
        </div>

        <Menu
          align="right"
          trigger={(open) => (
            <button className="icon-btn" onClick={open} title="Notificações">
              <Icon name="bell" />
              {(slaBreaches.length > 0 || unassigned.length > 0) && <span className="dot" />}
            </button>
          )}
        >
          {(close) => (
            <div style={{ width: 300 }}>
              <div className="pop-h">Notificações</div>
              {slaBreaches.length > 0 && (
                <button
                  className="notif"
                  onClick={() => {
                    close()
                  }}
                >
                  <span className="ni" style={{ background: '#fff7ed', color: '#b45309' }}>
                    <Icon name="warn" size={15} />
                  </span>
                  <span>
                    <span className="t">{slaBreaches.length} conversas aguardando há mais de 30 min</span>
                    <span className="s">{slaBreaches.slice(0, 3).map((c) => c.customerName).join(', ')}</span>
                  </span>
                </button>
              )}
              {unassigned.length > 0 && (
                <button className="notif" onClick={close}>
                  <span className="ni" style={{ background: '#eef2ff', color: '#3f4ae0' }}>
                    <Icon name="check2" size={15} />
                  </span>
                  <span>
                    <span className="t">{unassigned.length} conversas não atribuídas</span>
                    <span className="s">Distribua entre a equipe</span>
                  </span>
                </button>
              )}
              {slaBreaches.length === 0 && unassigned.length === 0 && (
                <div className="empty" style={{ padding: 18 }}>
                  Tudo em dia por aqui.
                </div>
              )}
            </div>
          )}
        </Menu>

        <Menu
          align="right"
          trigger={(open) => (
            <button className="me-btn" onClick={open}>
              <span style={{ position: 'relative', display: 'inline-flex' }}>
                <AgentAvatar
                  name={user?.name ?? null}
                  color={user?.avatarColor}
                  avatarUrl={user?.avatarUrl}
                  size={28}
                />
                <span className="me-status" style={{ background: statusColor }} />
              </span>
              <span className="nm">{user?.name}</span>
              <Icon name="chev" size={13} />
            </button>
          )}
        >
          {(close) => (
            <>
              <div className="pop-h">Status</div>
              {STATUS_OPTIONS.map(([s, c]) => (
                <button
                  key={s}
                  className={`pop-item ${status === s ? 'on' : ''}`}
                  onClick={() => {
                    setStatus(s)
                    close()
                  }}
                >
                  <span className="stat-dot" style={{ background: c }} />
                  {s}
                </button>
              ))}
              <div className="pop-sep" />
              <button
                className="pop-item"
                onClick={() => {
                  onOpenSection('config')
                  close()
                }}
              >
                Configurações
              </button>
              <button
                className="pop-item"
                onClick={() => {
                  setChangingPhoto(true)
                  close()
                }}
              >
                Alterar foto
              </button>
              <button
                className="pop-item"
                onClick={() => {
                  setChangingPassword(true)
                  close()
                }}
              >
                Alterar senha
              </button>
              <div className="pop-sep" />
              <button className="pop-item" style={{ color: '#b42318' }} onClick={() => logout()}>
                Sair
              </button>
            </>
          )}
        </Menu>
      </div>

      {changingPhoto && user && (
        <div className="modal-back" onClick={() => setChangingPhoto(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ width: 'min(380px, calc(100vw - 32px))' }}>
            <div className="modal-h">
              Sua foto
              <button className="icon-btn" onClick={() => setChangingPhoto(false)} title="Fechar">
                <Icon name="x" size={14} />
              </button>
            </div>
            <div className="modal-b">
              <p className="modal-note">
                Ela aparece ao lado de cada mensagem que você envia, para a equipe saber de quem é
                cada resposta sem precisar ler o nome.
              </p>
              <AvatarPicker
                userId={user.id}
                name={user.name}
                color={user.avatarColor}
                avatarUrl={user.avatarUrl}
                size={56}
              />
            </div>
          </div>
        </div>
      )}

      {changingPassword && (
        <ChangePassword
          onClose={() => setChangingPassword(false)}
          onDone={() => {
            setChangingPassword(false)
            toast('Senha alterada. Outros dispositivos foram desconectados.')
          }}
        />
      )}
    </header>
  )
}
